import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useIdleTimer } from "react-idle-timer";
import { ConfirmationModal } from "@monorepo/components";
import { INACTIVITY_MINUTES_TO_LOGOUT } from "@monorepo/constants";
import { useLogout } from "@monorepo/hooks";
import { setLastActive } from "@monorepo/slices/auth";
import { differenceInMinutes } from "date-fns";

import { useAppDispatch, useAppSelector } from "hooks";
import { persistor } from "store";
const timeout = 60 * INACTIVITY_MINUTES_TO_LOGOUT * 1000;

export const IdleTimeChecker: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { logout } = useLogout();
  const token = useAppSelector((state) => state.authSlice.access_token);
  const lastActive = useAppSelector((state) => state.authSlice.last_active);
  const [initialLastActive, setInitialLastActive] = useState<number | undefined>(
    token && lastActive ? lastActive : undefined
  );
  const [remaining, setRemaining] = useState(timeout);
  const { getRemainingTime, getLastActiveTime } = useIdleTimer({
    timeout,
  });

  useEffect(() => {
    if (!token) {
      persistor.pause();
      persistor.flush().then(() => persistor.purge());
      persistor.persist();
    }
  }, [token]);

  useEffect(() => {
    setRemaining(Math.round(getRemainingTime() / 1000));
    dispatch(setLastActive(getLastActiveTime()));
    const timer = setInterval(() => {
      setRemaining(Math.round(getRemainingTime() / 1000));
      dispatch(setLastActive(getLastActiveTime()));
    }, 1000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    //checking whether to logout logged in user with a help of remaining const
    if (token && remaining === 0) {
      logout();
      setInitialLastActive(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, remaining]);

  useEffect(() => {
    //checking whether to logout user that is trying to login after refresh
    if (!!initialLastActive) {
      const diffInMinutes = differenceInMinutes(new Date(), new Date(initialLastActive), { roundingMethod: "floor" });
      if (token && diffInMinutes >= INACTIVITY_MINUTES_TO_LOGOUT) {
        logout();
        setInitialLastActive(undefined);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, initialLastActive]);

  const showIdleWarning = !!token && remaining <= 60;
  const userLoggedOutForBeingIdle = !token && initialLastActive === 0;
  const showModal = showIdleWarning || userLoggedOutForBeingIdle;

  return (
    <>
      {showModal && (
        <ConfirmationModal
          textAlign={userLoggedOutForBeingIdle ? "center" : "left"}
          open={showModal}
          onClose={() => {
            setRemaining(timeout);
            setInitialLastActive(undefined);
          }}
        >
          {showIdleWarning
            ? t("common.will_be_logged_out_in", { remaining })
            : userLoggedOutForBeingIdle
            ? t("common.your_session_expired")
            : null}
        </ConfirmationModal>
      )}
    </>
  );
};
